import React, { useEffect, useState } from 'react';
import { X, Smartphone, Share } from 'lucide-react';

const DISMISS_KEY = 'pwa_install_dismissed_at';
const DISMISS_DAYS = 14; // re-show 2 weeks after dismissal

function isStandalone() {
    return window.matchMedia('(display-mode: standalone)').matches
        || window.navigator.standalone === true;
}

function isIos() {
    return /iPhone|iPad|iPod/.test(window.navigator.userAgent) && !window.MSStream;
}

function recentlyDismissed() {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (!ts) return false;
    const days = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return days < DISMISS_DAYS;
}

export default function InstallPrompt() {
    const [deferred, setDeferred] = useState(null); // Android/Chrome BeforeInstallPromptEvent
    const [showIos, setShowIos] = useState(false);
    const [open, setOpen] = useState(true);

    useEffect(() => {
        if (isStandalone() || recentlyDismissed()) return;

        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferred(e);
        };
        window.addEventListener('beforeinstallprompt', onBeforeInstall);

        if (isIos()) {
            const t = setTimeout(() => setShowIos(true), 4000);
            return () => {
                clearTimeout(t);
                window.removeEventListener('beforeinstallprompt', onBeforeInstall);
            };
        }

        return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
    }, []);

    const dismiss = () => {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
        setOpen(false);
    };

    const install = async () => {
        if (!deferred) return;
        try {
            deferred.prompt();
            const choice = await deferred.userChoice;
            if (choice.outcome === 'accepted') setOpen(false);
            setDeferred(null);
        } catch {
            setOpen(false);
        }
    };

    if (!open) return null;
    if (!deferred && !showIos) return null;

    return (
        <div
            className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-50 rounded-2xl border border-sand bg-white shadow-lg p-4 flex items-start gap-3"
            data-testid="install-prompt"
        >
            <div className="w-9 h-9 rounded-full bg-sage-soft flex items-center justify-center text-sage-dark shrink-0">
                <Smartphone className="w-4 h-4"/>
            </div>
            <div className="flex-1 text-sm">
                <p className="font-medium text-ink">Legg til på hjem-skjerm</p>
                {deferred ? (
                    <p className="text-inkSoft text-xs mt-1 leading-relaxed">
                        Få Stillerom som et eget ikon på telefonen – ingen App Store, ingen nedlasting.
                    </p>
                ) : (
                    <p className="text-inkSoft text-xs mt-1 leading-relaxed">
                        Trykk <span className="inline-flex items-center gap-1 align-middle"><Share className="w-3 h-3 inline"/> Del</span> i Safari, deretter <strong>"Legg til på hjem-skjermen"</strong>.
                    </p>
                )}
                {deferred && (
                    <button
                        onClick={install}
                        className="mt-3 px-4 py-1.5 rounded-full bg-sage hover:bg-sage-dark text-bone text-xs font-medium"
                        data-testid="install-prompt-btn"
                    >
                        Installer Stillerom
                    </button>
                )}
            </div>
            <button
                onClick={dismiss}
                className="p-1 text-inkSoft hover:text-ink shrink-0"
                aria-label="Lukk"
                data-testid="install-prompt-close"
            >
                <X className="w-4 h-4"/>
            </button>
        </div>
    );
}
